import { Badge, Caption1, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { SectionTitle } from "@/components/ui-typography";
import { Add16Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useState, type DragEvent } from "react";
import { FuiButton, FuiInput } from "@/components/fui-control";
import type { ExecutionTask } from "@/engine/types";
import { ProjectTaskCard } from "@/features/tasks/TaskCard";
import { groupAxisIcon } from "@/features/tasks/task-glyphs";
import { EmptyState, useTaskContext } from "@/features/tasks/task-ui";
import { isScopeGroupKey, type GroupKey, type TaskGroup } from "@/features/tasks/task-query";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { centerFill, shellGutter } from "@/shell/content-frame";
import { useProjectStore } from "@/store/project-store";

const DND_TYPE = "application/x-wavex-execution-task";

const useStyles = makeStyles({
  lanes: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    gap: tokens.spacingHorizontalM,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalL}`,
    overflowX: "auto",
    overflowY: "hidden",
    alignItems: "stretch",
  },
  column: {
    width: "280px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.border("1px", "solid", "transparent"),
    minHeight: 0,
    overflow: "hidden",
  },
  columnOver: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  columnHead: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
  },
  columnTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minHeight: 0,
    flex: 1,
    overflowX: "hidden",
    overflowY: "auto",
    paddingBottom: tokens.spacingVerticalS,
  },
  draggable: {
    flexShrink: 0,
    cursor: "grab",
    ":active": { cursor: "grabbing" },
    "& > *": { height: "auto" },
  },
  dragging: {
    opacity: 0.4,
  },
  dropBefore: {
    boxShadow: `0 -3px 0 0 ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  addColumn: {
    width: "240px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
  },
  quick: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
  },
  empty: {
    ...centerFill,
    padding: tokens.spacingHorizontalXXL,
  },
});

interface DragState {
  id: string;
  fromKey: string;
}

interface Props {
  tasks: ExecutionTask[];
  visibleCount: number;
  groups: TaskGroup[];
  axis: GroupKey;
  onClearFilter: () => void;
}

/** Planner-style board over execution tasks; the schedule leaf progress board lives in 진척 업데이트. */
export function TaskBoardView({ tasks, visibleCount, groups, axis, onClearFilter }: Props) {
  const styles = useStyles();
  const canEdit = usePlanSurface().editTask;
  const own = useOwnWork();
  const ctx = useTaskContext();
  const s = useProjectStore.getState;
  const [drag, setDrag] = useState<DragState | null>(null);
  const [over, setOver] = useState<{ key: string; beforeId: string | null } | null>(null);
  const [newBucket, setNewBucket] = useState("");

  const onDrop = (g: TaskGroup, beforeId: string | null) => {
    if (!drag) return;
    const patch: Partial<ExecutionTask> = g.patch ? { ...g.patch } : {};
    if (axis === "assignee" && g.patch?.assignees) {
      const cur = tasks.find((t) => t.id === drag.id)?.assignees ?? [];
      patch.assignees = g.key === "" ? [] : [...cur.filter((a) => a !== g.key && a !== drag.fromKey), g.key];
    }
    if (isScopeGroupKey(axis) && g.patch?.linkedTaskIds) {
      const cur = tasks.find((t) => t.id === drag.id)?.linkedTaskIds ?? [];
      patch.linkedTaskIds = g.key === "" ? [] : [...new Set([...cur, g.key])];
    }
    if (axis === "due" && !g.patch) {
      setDrag(null);
      setOver(null);
      return;
    }
    s().moveExecutionTask(drag.id, patch, g.tasks.map((t) => t.id), beforeId);
    setDrag(null);
    setOver(null);
  };

  const dragProps = (t: ExecutionTask, g: TaskGroup) => ({
    draggable: canEdit && own.executionOwn(t.assignees),
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.setData(DND_TYPE, t.id);
      e.dataTransfer.effectAllowed = "move";
      setDrag({ id: t.id, fromKey: g.key });
    },
    onDragEnd: () => {
      setDrag(null);
      setOver(null);
    },
    onDragOver: (e: DragEvent) => {
      if (!drag || drag.id === t.id) return;
      e.preventDefault();
      e.stopPropagation();
      setOver({ key: g.key, beforeId: t.id });
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDrop(g, t.id);
    },
  });

  if (tasks.length === 0) {
    return (
      <div className={styles.empty}>
        <EmptyState
          title="보드가 비어 있습니다"
          hint={own.canCreateTask ? "Task를 만들면 버킷·상태·우선순위·담당자 열로 옮기며 관리할 수 있습니다." : "이 플랜에 등록된 Task가 없습니다."}
          action={
            own.canCreateTask ? (
              <FuiButton appearance="primary" icon={<Add16Regular />} onClick={() => s().addExecutionTask({ bucketId: s().project.planner.buckets[0]?.id })}>
                첫 Task 만들기
              </FuiButton>
            ) : undefined
          }
        />
      </div>
    );
  }

  if (visibleCount === 0) {
    return (
      <div className={styles.empty}>
        <EmptyState
          title="필터와 일치하는 Task가 없습니다"
          hint="필터를 지우거나 다른 조건을 선택하세요."
          action={<FuiButton onClick={onClearFilter}>필터 지우기</FuiButton>}
        />
      </div>
    );
  }

  return (
    <div className={styles.lanes}>
      {groups.map((g) => {
        const droppable = !!g.patch || axis === "none";
        const isOver = over?.key === g.key;
        return (
          <section
            key={g.key || "__none"}
            className={mergeClasses(styles.column, isOver && droppable && styles.columnOver)}
            aria-label={`${g.label} ${g.tasks.length}개`}
            onDragOver={(e) => {
              if (!canEdit || !drag || !droppable) return;
              e.preventDefault();
              if (!over || over.key !== g.key) setOver({ key: g.key, beforeId: null });
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              if (over?.key === g.key) setOver(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (droppable) onDrop(g, over?.key === g.key ? over.beforeId : null);
            }}
          >
            <header className={styles.columnHead}>
              {groupAxisIcon(axis)}
              <SectionTitle className={styles.columnTitle} title={g.label}>
                {g.label}
              </SectionTitle>
              <Badge appearance="tint" color="informative" size="small">
                {g.tasks.length}
              </Badge>
              {axis === "bucket" && g.key && <BucketMenu id={g.key} name={g.label} />}
            </header>
            <div className={styles.list}>
              {g.tasks.map((t) => (
                <div key={t.id} className={mergeClasses(styles.draggable, drag?.id === t.id && styles.dragging, isOver && over?.beforeId === t.id && styles.dropBefore)} {...dragProps(t, g)}>
                  <ProjectTaskCard task={t} ctx={ctx} axis={axis} />
                </div>
              ))}
              {g.tasks.length === 0 && (
                <Caption1 style={{ color: tokens.colorNeutralForeground4, padding: tokens.spacingHorizontalS }}>
                  {droppable ? "여기로 끌어다 놓기" : "해당 없음"}
                </Caption1>
              )}
            </div>
            {g.patch && <QuickAdd patch={g.patch} axis={axis} groupKey={g.key} />}
          </section>
        );
      })}
      {axis === "bucket" && (
        <div className={styles.addColumn}>
          <div className={styles.quick}>
            <FuiInput
              placeholder="새 버킷"
              value={newBucket}
              aria-label="새 버킷 이름"
              onChange={(e) => setNewBucket(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newBucket.trim()) {
                  s().addBucket(newBucket);
                  setNewBucket("");
                }
              }}
            />
            <FuiButton
              icon={<Add16Regular />}
              disabled={!newBucket.trim()}
              onClick={() => {
                s().addBucket(newBucket);
                setNewBucket("");
              }}
            >
              버킷
            </FuiButton>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickAdd({ patch, axis, groupKey }: { patch: Partial<ExecutionTask>; axis: GroupKey; groupKey: string }) {
  const styles = useStyles();
  const s = useProjectStore.getState;
  const [text, setText] = useState("");
  const add = () => {
    const title = text.trim();
    if (!title) return;
    const partial: Partial<ExecutionTask> = { title, ...patch };
    if (axis === "assignee") partial.assignees = groupKey ? [groupKey] : [];
    s().addExecutionTask(partial, { open: false });
    setText("");
  };
  return (
    <div className={styles.quick}>
      <FuiInput
        placeholder="Task 추가 후 ↩"
        value={text}
        aria-label="이 열에 Task 추가"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
      />
    </div>
  );
}

function BucketMenu({ id, name }: { id: string; name: string }) {
  const s = useProjectStore.getState;
  const [renaming, setRenaming] = useState(false);
  const [value, setValue] = useState(name);
  if (renaming) {
    return (
      <FuiInput
        autoFocus
        value={value}
        aria-label="버킷 이름"
        style={{ width: 140 }}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (value.trim() && value !== name) s().renameBucket(id, value);
          setRenaming(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            setValue(name);
            setRenaming(false);
          }
        }}
      />
    );
  }
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label={`${name} 버킷 메뉴`} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem onClick={() => setRenaming(true)}>이름 바꾸기</MenuItem>
          <MenuItem onClick={() => s().moveBucket(id, -1)}>왼쪽으로</MenuItem>
          <MenuItem onClick={() => s().moveBucket(id, 1)}>오른쪽으로</MenuItem>
          <MenuDivider />
          <MenuItem onClick={() => s().removeBucket(id)}>버킷 삭제 (Task는 유지)</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
